import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Avatar, Button, Dropdown, Layout, Modal, Space, Typography, type MenuProps } from 'antd';
import {
  HomeOutlined,
  LogoutOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';

import ThemeToggle from './ThemeToggle.tsx';
import { useAppDispatch, useAppSelector } from '@store/store.tsx';
import { logout } from '@store/slices/authSlice.tsx';
import { selectProfile } from '@store/slices/userSlice';
import {
  selectSiteApps,
  selectSiteSettings,
  selectSiteTitle,
} from '@store/slices/settingsSlice.tsx';

import './AccountLayout.css';

const { Content, Footer, Header } = Layout;

type AccountLayoutProps = {
  children?: ReactNode;
};

type PublicNavigationItem = {
  key: string;
  label: string;
  href: string;
  icon: ReactNode;
};

const ACCOUNT_MOBILE_QUERY = '(max-width: 899px)';

const useMobileAccountLayout = () => {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(ACCOUNT_MOBILE_QUERY).matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia(ACCOUNT_MOBILE_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isMobile;
};

type AccountUserMenuProps = {
  navigationItems: PublicNavigationItem[];
};

const AccountUserMenu = ({ navigationItems }: AccountUserMenuProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const profile = useAppSelector(selectProfile);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const displayName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ')
    || profile?.username
    || 'Mi cuenta';

  const items = useMemo<NonNullable<MenuProps['items']>>(() => [
    {
      key: 'identity',
      type: 'group',
      label: (
        <div className="account-layout__user-identity">
          <strong>{displayName}</strong>
          {profile?.email ? <span>{profile.email}</span> : null}
        </div>
      ),
    },
    ...(navigationItems.length > 0 ? [
      { type: 'divider' as const },
      ...navigationItems.map((item) => ({
        key: `public:${item.key}`,
        label: item.label,
        icon: item.icon,
      })),
    ] : []),
    { type: 'divider' },
    { key: 'logout', label: 'Salir', icon: <LogoutOutlined />, danger: true },
  ], [displayName, navigationItems, profile?.email]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      setLogoutOpen(true);
      return;
    }

    const navigationItem = navigationItems.find((item) => `public:${item.key}` === key);
    if (navigationItem) navigate(navigationItem.href);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      setLogoutOpen(false);
    } catch {
      // The auth guard will keep the current session when logout fails.
    }
  };

  return (
    <>
      <Dropdown menu={{ items, onClick: handleMenuClick }} trigger={['click']} placement="bottomRight">
        <Button
          type="text"
          className="account-layout__user-button"
          aria-label="Abrir menú de cuenta"
          icon={profile?.avatar_url ? <Avatar size="small" src={profile.avatar_url} /> : <UserOutlined />}
        >
          <span className="account-layout__user-name">{displayName}</span>
        </Button>
      </Dropdown>
      <Modal
        title="¿Cerrar sesión?"
        open={logoutOpen}
        onOk={() => void handleLogout()}
        onCancel={() => setLogoutOpen(false)}
        okText="Sí, salir"
        cancelText="Cancelar"
      >
        <Typography.Text>Se cerrará tu sesión actual.</Typography.Text>
      </Modal>
    </>
  );
};

const AccountLayout = ({ children }: AccountLayoutProps) => {
  const isMobile = useMobileAccountLayout();
  const apps = useAppSelector(selectSiteApps) ?? [];
  const siteSettings = useAppSelector(selectSiteSettings);
  const siteTitle = useAppSelector(selectSiteTitle);
  const [bannerFailed, setBannerFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const hasCms = apps.includes('cms');
  const hasEshop = apps.includes('eshop');
  const publicHome = hasCms ? '/web' : hasEshop ? '/eshop/catalogue' : null;
  const bannerUrl = siteSettings?.dash_banner?.thumbnails?.urls.large ?? null;
  const logoUrl = siteSettings?.logo?.thumbnails?.default ?? null;
  const businessName = siteSettings?.name ?? siteTitle ?? 'Mi cuenta';

  const navigationItems = useMemo(() => [
    ...(hasCms ? [{ key: 'home', label: 'Inicio', href: '/web', icon: <HomeOutlined /> }] : []),
    ...(hasEshop ? [{ key: 'shop', label: 'Tienda', href: '/eshop/catalogue', icon: <ShopOutlined /> }] : []),
  ], [hasCms, hasEshop]);

  return (
    <Layout className="account-layout">
      <Header className="account-layout__header">
        <div className="account-layout__header-inner w-max-xxl mx-auto">
          <div className="account-layout__brand-wrap">
            {publicHome ? (
              <Link to={publicHome} className="account-layout__brand" aria-label={`Ir a ${businessName}`}>
                {bannerUrl && !bannerFailed ? (
                  <img
                    src={bannerUrl}
                    alt={businessName}
                    className="account-layout__banner"
                    onError={() => setBannerFailed(true)}
                  />
                ) : logoUrl && !logoFailed ? (
                  <img
                    src={logoUrl}
                    alt={businessName}
                    className="account-layout__logo"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <span className="account-layout__brand-name">{businessName}</span>
                )}
              </Link>
            ) : (
              <span className="account-layout__brand-name">{businessName}</span>
            )}
          </div>

          <nav className="account-layout__navigation" aria-label="Navegación pública">
            {navigationItems.map((item) => (
              <Link key={item.key} to={item.href} className="account-layout__nav-link">
                {item.icon}
                {item.label}
              </Link>
            ))}
          </nav>

          <Space className="account-layout__actions" size={4}>
            <ThemeToggle size="small" />
            <AccountUserMenu navigationItems={isMobile ? navigationItems : []} />
          </Space>
        </div>
      </Header>
      <Content className="account-layout__content w-max-xxl mx-auto">{children}</Content>
      <Footer className="account-layout__footer">Ahora Virtual ©{new Date().getFullYear()}</Footer>
    </Layout>
  );
};

export default AccountLayout;
